import { MAX_EQUIPPED_GEAR, formatGearBonus, getGearBonuses, toggleGearItem, type GearBonus, type GearDefinition, type GearInstance, type UserGear } from './gear';

export interface GearPageState {
  userId: string;
  gear: UserGear | null;
  loading: boolean;
  saving: boolean;
  error: string;
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[character]!);
}

function renderItem(item: GearDefinition, instance: GearInstance, equipped: boolean, disabled: boolean, location: string): string {
  return `
    <li class="gear-item${equipped ? ' gear-item-equipped' : ''}">
      <div class="gear-image-wrap"><img src="${escapeHtml(item.image)}" alt="" width="128" height="128" /></div>
      <h3>${escapeHtml(item.name)}</h3>
      ${renderGearBonuses(instance.bonus)}
      <button id="gear-${location}-${escapeHtml(instance.id)}" class="btn ${equipped ? 'btn-secondary' : ''}" type="button"
        data-gear-item="${escapeHtml(instance.id)}" data-gear-definition="${item.id}" aria-label="${equipped ? 'Unequip' : 'Equip'} ${escapeHtml(item.name)}, ${escapeHtml(formatGearBonus(instance.bonus))}"
        ${disabled ? 'disabled' : ''}>${equipped ? 'Unequip' : 'Equip'}</button>
    </li>`;
}

export function renderGearBonuses(bonus: GearBonus): string {
  const bonuses = getGearBonuses(bonus);
  if (bonuses.length === 1) return `<p class="gear-bonus">${escapeHtml(formatGearBonus(bonus))}</p>`;
  return `<div class="gear-bonus"><ul class="gear-bonus-list">${bonuses.map((entry) =>
    `<li>+${entry.points} ${escapeHtml(entry.activity)} points</li>`).join('')}</ul><p>Once per activity per workout, when you log that activity.</p></div>`;
}

export function renderGearContents(state: GearPageState): string {
  const { gear, loading, saving, error } = state;
  if (!gear) return `
    <section class="card gear-loading" aria-busy="${loading}">
      ${loading ? '<p role="status">Loading your gear…</p>' : ''}
      ${error ? `<p role="alert">${escapeHtml(error)}</p><button id="gear-retry-btn" class="btn" type="button">Try Again</button>` : ''}
    </section>`;

  const equippedItems = gear.equippedInstanceIds.map((id) => gear.items.find((item) => item.id === id)!).filter(Boolean);
  const renderInstance = (instance: GearInstance, location: string, disabled: boolean) => {
    const item = gear.catalog.find((item) => item.id === instance.gearId);
    return item ? renderItem(item, instance, gear.equippedInstanceIds.includes(instance.id), disabled, location) : '';
  };
  const slots = equippedItems.map((item) => renderInstance(item, 'equipped', saving));
  for (let index = slots.length; index < MAX_EQUIPPED_GEAR; index += 1) {
    slots.push('<li class="gear-item gear-slot-empty"><span aria-hidden="true">＋</span><p>Empty slot</p></li>');
  }
  const inventory = gear.items.map((item) =>
    renderInstance(item, 'inventory', saving || toggleGearItem(gear, item.id) === null),
  ).join('');

  return `
    <section class="card gear-equipped-section" aria-labelledby="gear-equipped-title" aria-busy="${saving}">
      <div class="gear-section-heading"><h2 id="gear-equipped-title">Equipped Gear</h2><strong class="gear-count">${equippedItems.length} / ${MAX_EQUIPPED_GEAR}</strong></div>
      <p class="card-copy">Choose up to five items, one per family. Equipping another roll or color replaces its family member.</p>
      <p class="card-copy">Your equipped gear locks when you create or join a workout. Later equipment changes apply to future workouts.</p>
      <ul class="gear-equipped-grid">${slots.join('')}</ul>
      <div class="gear-feedback">
        <p role="status">${saving ? 'Saving…' : equippedItems.length === MAX_EQUIPPED_GEAR ? 'All five slots are filled. Unequip an item to make room, or switch a variant.' : ''}</p>
        ${error ? `<p class="gear-error" role="alert">${escapeHtml(error)}</p>` : ''}
      </div>
    </section>
    <section class="card gear-inventory-section" aria-labelledby="gear-inventory-title" aria-busy="${saving}">
      <div class="gear-section-heading"><h2 id="gear-inventory-title">Your Gear</h2><span>${gear.items.length} items</span></div>
      ${inventory ? `<ul class="gear-inventory-grid">${inventory}</ul>` : '<p class="card-copy">Your gear collection is empty. Buy your first item in the shop.</p>'}
      <a href="/?view=shop" data-open-shop>Visit the Gear Shop</a>
    </section>`;
}
